import React, { useState, useEffect } from "react";
import { format } from "date-fns";
import DashboardLayout from "./DashboardLayout/DasboardLayout";
import { API_URL, API_URL_UPLOADS } from "./config";

const AllocateTrips = () => {
  const [trips, setTrips] = useState([]);
  const [filteredTrips, setFilteredTrips] = useState([]);
  const [orderDateFrom, setOrderDateFrom] = useState("");
  const [orderDateTo, setOrderDateTo] = useState("");
  const [commercialValueCategory, setCommercialValueCategory] = useState("all");
  const [tripStatus, setTripStatus] = useState("all");
  const [tripCount, setTripCount] = useState(0);
  const [drivers, setDrivers] = useState([]);
  const [selectedDrivers, setSelectedDrivers] = useState({});
  const [allocatedTrips, setAllocatedTrips] = useState({});

  const fetchData = () => {
    fetch(`${API_URL}/trip`)
      .then((response) => {
        if (!response.ok) {
          throw new Error("Network response was not ok");
        }
        return response.json();
      })
      .then((data) => {
        setTrips(data);
        setFilteredTrips(data);
        const allocations = {};
        data.forEach((trip) => {
          if (trip.status !== "New Order" && trip.status !== "Just In") {
            allocations[trip.trip_id] = trip.driver_id;
          }
        });
        setAllocatedTrips(allocations);
      })
      .catch((error) => console.error("Error fetching trips:", error));

    fetch("http://localhost:3011/driver")
      .then((response) => response.json())
      .then((data) => {
        const readyDrivers = data.filter(
          (driver) => driver.driving_status === "ready"
        );
        setDrivers(readyDrivers);
      })
      .catch((error) => console.error("Error fetching drivers:", error));
  };

  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, 30000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    let filtered = [...trips];

    if (orderDateFrom && orderDateTo) {
      filtered = filtered.filter((trip) => {
        const tripDate = new Date(trip.request_start_datetime);
        const fromDate = new Date(orderDateFrom);
        const toDate = new Date(orderDateTo);
        return tripDate >= fromDate && tripDate <= toDate;
      });
    }

    if (commercialValueCategory !== "all") {
      filtered = filtered.filter(
        (trip) =>
          trip.commercial_value_delivery_category === commercialValueCategory
      );
    }

    if (tripStatus !== "all") {
      filtered = filtered.filter((trip) => trip.status === tripStatus);
    }

    setFilteredTrips(filtered);
    setTripCount(filtered.length);
  }, [orderDateFrom, orderDateTo, commercialValueCategory, tripStatus, trips]);

  const statuses = ["all", ...new Set(trips.map((trip) => trip.status))];
  const commercialCategories = [
    "all",
    ...new Set(
      trips
        .map((trip) => trip.commercial_value_delivery_category)
        .filter(Boolean)
    ),
  ];

  const handleDriverChange = (tripId, driverId) => {
    setSelectedDrivers((prev) => ({ ...prev, [tripId]: driverId }));
  };

  const handleAllocate = async (tripId) => {
    const driverId = selectedDrivers[tripId];
    if (!driverId) return;

    try {
      const status = "InTransit";

      await fetch(
        `http://localhost:3011/trip/updateStatusAndDriver/${tripId}`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ driver_id: driverId, status }),
        }
      );

      await fetch(`http://localhost:3011/driver/driving_status/${driverId}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ driving_status: status }),
      });

      setAllocatedTrips((prev) => ({ ...prev, [tripId]: driverId }));
      alert("Trip successfully allocated.");
    } catch (error) {
      console.error("Allocation error:", error);
    }
  };

  return (
    <DashboardLayout>
      <div className="p-6 bg-white dark:bg-gray-800 min-h-screen">
        <h1 className="text-3xl font-bold mb-6 text-[#000] dark:text-white">
          Trip Allocation
        </h1>

        <div className="mb-6 bg-blue-100 border border-blue-300 text-blue-800 text-center p-4 rounded-lg">
          <strong>{tripCount}</strong> trips displayed
        </div>

        <div className="mb-6 grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="flex flex-col">
            <label className="mb-2 text-sm font-medium text-gray-700 dark:text-gray-200">
              Order Date From
            </label>
            <input
              type="date"
              className="p-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
              value={orderDateFrom}
              onChange={(e) => setOrderDateFrom(e.target.value)}
            />
          </div>
          <div className="flex flex-col">
            <label className="mb-2 text-sm font-medium text-gray-700 dark:text-gray-200">
              Order Date To
            </label>
            <input
              type="date"
              className="p-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
              value={orderDateTo}
              onChange={(e) => setOrderDateTo(e.target.value)}
            />
          </div>
          <div className="flex flex-col">
            <label className="mb-2 text-sm font-medium text-gray-700 dark:text-gray-200">
              Commercial Value Category
            </label>
            <select
              className="p-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
              value={commercialValueCategory}
              onChange={(e) => setCommercialValueCategory(e.target.value)}
            >
              {commercialCategories.map((category) => (
                <option key={category} value={category}>
                  {category === "all" ? "All Categories" : category}
                </option>
              ))}
            </select>
          </div>
          <div className="flex flex-col">
            <label className="mb-2 text-sm font-medium text-gray-700 dark:text-gray-200">
              Status
            </label>
            <select
              className="p-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
              value={tripStatus}
              onChange={(e) => setTripStatus(e.target.value)}
            >
              {statuses.map((status) => (
                <option key={status} value={status}>
                  {status === "all" ? "All Statuses" : status}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="overflow-x-auto shadow-md rounded-lg">
          <table className="w-full text-sm text-left text-gray-700 dark:text-gray-300">
            <thead className="text-xs text-gray-700 uppercase bg-gray-100 dark:bg-gray-700 dark:text-gray-300">
              <tr>
                <th className="px-6 py-3">Trip ID</th>
                <th className="px-6 py-3">Customer ID</th>
                <th className="px-6 py-3">Request Start</th>
                <th className="px-6 py-3">Status</th>
                <th className="px-6 py-3">Delivery Details</th>
                <th className="px-6 py-3">Origin - Destination</th>
                <th className="px-6 py-3">Distance</th>
                <th className="px-6 py-3">Payment Type</th>
                <th className="px-6 py-3">Assign Driver</th>
                <th className="px-6 py-3">Action</th>
              </tr>
            </thead>
            <tbody>
              {filteredTrips.map((trip) => {
                const isAllocated = trip.status !== "New Order" && trip.status !== "Just In";
                const driverId = allocatedTrips[trip.trip_id];
                const selectedDriver = drivers.find(d => d.driver_id === driverId);

                return (
                  <tr
                    key={trip.trip_id}
                    className="bg-white border-b dark:bg-gray-800 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600"
                  >
                    <td className="px-6 py-4">{trip.trip_id}</td>
                    <td className="px-6 py-4">{trip.cust_id}</td>
                    <td className="px-6 py-4">
                      {trip.request_start_datetime
                        ? format(
                            new Date(trip.request_start_datetime),
                            "MMM dd, yyyy HH:mm"
                          )
                        : "N/A"}
                    </td>
                    <td className="px-6 py-4">{trip.status}</td>
                    <td className="px-6 py-4">{trip.deliveray_details}</td>
                    <td className="px-6 py-4">
                      <b>From:</b> {trip.origin_location} <br /> <b>To:</b> {trip.dest_location}
                    </td>
                    <td className="px-6 py-4">{trip.distance} km</td>
                    <td className="px-6 py-4">{trip.payment_type}</td>
                    <td className="px-6 py-4">
                      {isAllocated ? (
                        <div>
                          {selectedDriver
                            ? `${selectedDriver.name} ${selectedDriver.surname}`
                            : "Driver Allocated"}
                        </div>
                      ) : (
                        <select
                          className="p-2 border rounded"
                          value={selectedDrivers[trip.trip_id] || ""}
                          onChange={(e) =>
                            handleDriverChange(trip.trip_id, e.target.value)
                          }
                          disabled={isAllocated}
                        >
                          <option value="">Select Driver</option>
                          {drivers.map((driver) => (
                            <option
                              key={driver.driver_id}
                              value={driver.driver_id}
                            >
                              {driver.name} {driver.surname} - {driver.driver_id}
                            </option>
                          ))}
                        </select>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      {isAllocated ? (
                        <button
                          className="px-4 py-2 rounded bg-gray-500 text-white cursor-not-allowed"
                          disabled
                        >
                          Allocated
                        </button>
                      ) : (
                        <button
                          className={`px-4 py-2 rounded text-white ${
                            selectedDrivers[trip.trip_id]
                              ? "bg-green-600 hover:bg-green-700"
                              : "bg-gray-400 cursor-not-allowed"
                          }`}
                          disabled={!selectedDrivers[trip.trip_id]}
                          onClick={() => handleAllocate(trip.trip_id)}
                        >
                          {selectedDrivers[trip.trip_id] ? "Allocate" : "Decline"}
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {filteredTrips.length === 0 && (
          <div className="text-center py-4 text-gray-500 dark:text-gray-400">
            No trips found matching the selected filters.
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};

export default AllocateTrips;